// src/reports/CrossSiteSalesReport.jsx — Reporting → Location Reports → Cross Site Sales (Eric, 2026-10-28).
// One inventory, mirrored onto several stores: Soccer Post "Tinton Falls Warehouse" is the same stock as
// "Tinton Falls Warehouse (Virtual)" on U90, Surf, B2B, Stefans… but every store sells on its own. Per SKU:
// stock at the home location, Committed across every copy, and Units Sold on each store plus a Total.
// Works the same under any store at the top (the picker lists every store's locations).
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, BlockStack, InlineStack, Text, Button, TextField, Banner, Checkbox, Tooltip, Collapsible } from '@shopify/polaris';
import { useAuthFetch } from '../AuthComponent';
import { isoDaysAgo, todayIso, ReportDateRange, ReportPicker } from '../Reporting';

const PAGE = 200;
const fmtN = n => (n === null || n === undefined) ? '—' : Number(n).toLocaleString();
const fmtUSD = n => (n === null || n === undefined || Number.isNaN(Number(n))) ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
// 2026-10-28 (Eric): every column header has a plain-English tooltip (dotted underline, like Shopify's
// reports), and the same words sit in a "What the columns mean" panel that can be collapsed.
const FIXED = [
    { key: 'sku', label: 'SKU', tip: 'The variant\'s SKU. A SKU sold on several stores is one row: its sales on every store are added up.' },
    { key: 'product_title', label: 'Product', tip: 'The product name in Shopify.' },
    { key: 'variant_title', label: 'Variant', tip: 'The size or option of the product, e.g. 5 for a size 5 ball.' },
    { key: 'vendor', label: 'Vendor', tip: 'The brand or supplier set on the product in Shopify.' },
    { key: 'unit_cost', label: 'Cost', right: true, tip: 'What one unit costs us — the cost on the Shopify inventory item.' },
    { key: 'sp_price', label: 'SP Price', right: true, tip: 'What one unit sells for on Soccer Post right now. Blank when the SKU is not stocked at any Soccer Post Teams or Warehouse location.' },
    { key: 'committed', label: 'Committed', right: true, tip: 'Units already promised to orders that have not shipped yet, added up over the location and every store\'s copy of it (each store commits its own orders).' },
    { key: 'available', label: 'Available', right: true, tip: 'Units on the shelf that can still be sold: on hand minus committed and any held back. One count for the location — the copies on other stores mirror it.' },
    { key: 'on_hand', label: 'On Hand', right: true, tip: 'Every unit physically at the location, whether promised to an order or not.' },
    { key: 'incoming', label: 'Incoming', right: true, tip: 'Units on their way to the location (transfers and purchase orders not yet received).' },
];
const storeTip = st => (st.blank
    ? `You don't have access to ${st.label}'s copy of these locations, so its sales are not shown.`
    : `Units sold on ${st.label} in the date range from the selected locations — orders that were not cancelled. Returns are not taken off.`);
const TOTAL_TIP = 'Units sold on every store added together — what the location sold in the date range.';
const HeadTip = ({ tip, children }) => (tip
    ? <Tooltip content={tip} dismissOnMouseOut preferredPosition="above" width="wide">
        <span style={{ borderBottom: '1px dotted #8c9196', cursor: 'inherit' }}>{children}</span>
      </Tooltip>
    : children);
const GLOSSARY_KEY = 'crossSiteSales.glossaryOpen';

function useDebounced(value, ms) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
    return v;
}

export default function CrossSiteSalesReport() {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [sites, setSites] = useState([]);
    const [picked, setPicked] = useState(() => searchParams.getAll('site').filter(Boolean));
    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo, setDateTo] = useState(searchParams.get('to') || todayIso());
    const [product, setProduct] = useState(searchParams.get('product') || '');
    const [sku, setSku] = useState(searchParams.get('sku') || '');
    const [vendor, setVendor] = useState(searchParams.get('vendor') || '');
    const [untracked, setUntracked] = useState(searchParams.get('untracked') === '1');
    const [sort, setSort] = useState({ key: searchParams.get('sort') || 'total_sold', dir: searchParams.get('dir') === 'asc' ? 'asc' : 'desc' });
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [glossaryOpen, setGlossaryOpen] = useState(() => { try { return localStorage.getItem(GLOSSARY_KEY) !== '0'; } catch (_e) { return true; } });
    const toggleGlossary = () => setGlossaryOpen(o => { try { localStorage.setItem(GLOSSARY_KEY, o ? '0' : '1'); } catch (_e) { /* private mode */ } return !o; });
    const productQ = useDebounced(product, 400);
    const skuQ = useDebounced(sku, 400);

    useEffect(() => {
        fetchAuth('/api/reports/cross-site-sales/sites').then(async r => {
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
            setSites(j.sites || []);
            setPicked(p => p.filter(k => (j.sites || []).some(s => s.key === k)));
        }).catch(e => setErr(e.message));
    }, [fetchAuth]);

    const query = useCallback((extra = {}) => {
        const q = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, product: productQ, sku: skuQ, vendor,
                                        untracked: untracked ? '1' : '', sort: sort.key, dir: sort.dir, ...extra });
        picked.forEach(k => q.append('sites', k));
        return q;
    }, [picked, dateFrom, dateTo, productQ, skuQ, vendor, untracked, sort]);

    useEffect(() => {
        if (!picked.length) { setData(null); return; }
        let live = true;
        setBusy(true); setErr(null);
        fetchAuth(`/api/reports/cross-site-sales/rows?${query({ page: String(page), page_size: String(PAGE) })}`).then(async r => {
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
            if (live) setData(j);
        }).catch(e => live && setErr(e.message)).finally(() => live && setBusy(false));
        return () => { live = false; };
    }, [fetchAuth, query, page, picked.length]);

    useEffect(() => { setPage(1); }, [picked, dateFrom, dateTo, productQ, skuQ, vendor, untracked, sort]);
    useEffect(() => {
        const p = new URLSearchParams();
        picked.forEach(k => p.append('site', k));
        const set = (k, v, d = '') => { if (v && v !== d) p.set(k, v); };
        set('from', dateFrom); set('to', dateTo); set('product', product); set('sku', sku); set('vendor', vendor);
        set('untracked', untracked ? '1' : ''); set('sort', sort.key, 'total_sold'); set('dir', sort.dir, 'desc');
        if (p.toString() !== searchParams.toString()) setSearchParams(p, { replace: true });
    }, [picked, dateFrom, dateTo, product, sku, vendor, untracked, sort]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleSort = key => setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: ['sku', 'product_title', 'variant_title', 'vendor'].includes(key) ? 'asc' : 'desc' });
    const exportCsv = () => {
        fetchAuth(`/api/reports/cross-site-sales/export.csv?${query()}`).then(async r => {
            if (!r.ok) { const t = await r.text().catch(() => ''); let m = t; try { m = JSON.parse(t).detail || t; } catch (_e) { /* raw */ } setErr(m || `HTTP ${r.status}`); return; }
            const b = await r.blob();
            const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = `cross_site_sales_${dateFrom}_${dateTo}.csv`; a.click(); URL.revokeObjectURL(a.href);
        });
    };

    const stores = data?.stores || [];
    const pages = Math.max(1, Math.ceil((data?.total || 0) / PAGE));
    const th = (key, label, right, tip, extra = {}) => (
        <th key={key} onClick={() => toggleSort(key)}
            style={{ cursor: 'pointer', textAlign: right ? 'right' : 'left', userSelect: 'none', ...extra }}>
            <HeadTip tip={tip}>{label}</HeadTip>{sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
        </th>
    );
    const numTd = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
    const hidden = sites.filter(s => picked.includes(s.key) && s.hidden_stores.length);

    return (
        <BlockStack gap="400">
            <Card>
                <BlockStack gap="200">
                    <InlineStack align="space-between" blockAlign="center" wrap>
                        <Text as="h2" variant="headingMd">Cross Site Sales</Text>
                        <Button onClick={exportCsv} disabled={!data?.total}>Export CSV</Button>
                    </InlineStack>
                    <Text as="p" tone="subdued">
                        One inventory, several stores: a warehouse's stock lives on its home store and is mirrored as "(Virtual)" copies on the others (City Sports' locations are their own),
                        but every store sells on its own. Available, On Hand and Incoming are the home location's (nightly snapshot); Committed adds every store's copy;
                        Units Sold is live, per store — non-cancelled orders placed in the date range whose fulfillment sits at that store's copy of the location — and Total is their sum.
                        Draft and archived products are not counted, nor are items set to "Inventory not tracked" unless "Include untracked inventory" is ticked.
                    </Text>
                    <div>
                        <Button variant="plain" disclosure={glossaryOpen ? 'up' : 'down'} onClick={toggleGlossary}>
                            {glossaryOpen ? 'Hide what the columns mean' : 'What the columns mean'}
                        </Button>
                        <Collapsible open={glossaryOpen} id="css-glossary" transition={{ duration: '150ms' }}>
                            <div style={{ marginTop: 8, padding: '10px 14px', background: '#f6f6f7', borderRadius: 8 }}>
                                <dl style={{ display: 'grid', gridTemplateColumns: 'max-content minmax(0, 1fr)', gap: '6px 16px', margin: 0, fontSize: 13 }}>
                                    {FIXED.map(c => (<React.Fragment key={c.key}><dt style={{ fontWeight: 600 }}>{c.label}</dt><dd style={{ margin: 0 }}>{c.tip}</dd></React.Fragment>))}
                                    <dt style={{ fontWeight: 600 }}>Store columns</dt>
                                    <dd style={{ margin: 0 }}>One per store (Soccer Post, Surf, B2B, …): units sold on that store in the date range from the selected locations — orders that were not cancelled, counted when they were placed. Returns are not taken off. Blank when you don't have access to that store's copy.</dd>
                                    <dt style={{ fontWeight: 600 }}>Total Sold</dt><dd style={{ margin: 0 }}>{TOTAL_TIP}</dd>
                                    <dt style={{ fontWeight: 600 }}>Merged locations</dt>
                                    <dd style={{ margin: 0 }}>"Tinton Falls Warehouse (Soccer Post, Surf, Upper 90, B2B, Stefans)" is one stock that every listed store sells from — its "(Virtual)" copies. Stock is counted once; sales are counted on each store.</dd>
                                </dl>
                            </div>
                        </Collapsible>
                    </div>
                    {err && <Banner tone="critical" onDismiss={() => setErr(null)}>{err}</Banner>}
                    {hidden.length > 0 && <Banner tone="info">{hidden.map(s => `${s.label}: ${s.hidden_stores.join(', ')}`).join(' · ')} — you don't have access to those stores' copies, so their sales columns are blank.</Banner>}
                    <InlineStack gap="300" blockAlign="center" wrap>
                        <ReportPicker label="Location" options={sites.map(s => ({ label: s.label, value: s.key }))} value={picked} onChange={setPicked} multiple searchable nowrap />
                        <div style={{ minWidth: 220 }}><TextField label="Product" labelHidden placeholder="Product name…" value={product} onChange={setProduct} autoComplete="off" clearButton onClearButtonClick={() => setProduct('')} /></div>
                        <div style={{ minWidth: 180 }}><TextField label="SKU" labelHidden placeholder="SKU…" value={sku} onChange={setSku} autoComplete="off" clearButton onClearButtonClick={() => setSku('')} /></div>
                        <ReportPicker label="Vendor" options={[{ label: 'All vendors', value: '' }, ...((data?.vendors || (vendor ? [vendor] : [])).map(v => ({ label: v, value: v })))]} value={vendor} onChange={setVendor} searchable />
                        <Checkbox label="Include untracked inventory" checked={untracked} onChange={setUntracked} />
                    </InlineStack>
                    <InlineStack gap="200" blockAlign="center" wrap>
                        <Text as="span" tone="subdued">Units sold</Text>
                        <ReportDateRange dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} presets={['7d', '30d', '90d', 'ytd']} />
                        {data && <Text as="span" tone="subdued">{fmtN(data.total)} SKUs · {fmtN(data.totals?.total_sold)} units sold</Text>}
                        {busy && <Text as="span" tone="subdued">Loading…</Text>}
                    </InlineStack>
                </BlockStack>
            </Card>

            {!picked.length && <Card><Text as="p" tone="subdued">Pick one or more locations to load them.</Text></Card>}

            {picked.length > 0 && data && (
                <Card padding="0">
                    <div className="orders-summary-table-scroll" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                        <table className="orders-summary-table">
                            <thead>
                                <tr>
                                    {FIXED.map(c => th(c.key, c.label, c.right, c.tip))}
                                    {stores.map(st => th(`sold:${st.key}`, st.label, true, storeTip(st), { background: '#eef4fb' }))}
                                    {th('total_sold', 'Total Sold', true, TOTAL_TIP, { background: '#e3ecf9' })}
                                </tr>
                            </thead>
                            <tbody>
                                {data.rows.map(r => (
                                    <tr key={r.sku}>
                                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.sku}</td>
                                        <td>{r.product_title || '—'}</td>
                                        <td>{r.variant_title && r.variant_title !== 'Default Title' ? r.variant_title : ''}</td>
                                        <td>{r.vendor || '—'}</td>
                                        <td style={numTd}>{fmtUSD(r.unit_cost)}</td>
                                        <td style={numTd}>{fmtUSD(r.sp_price)}</td>
                                        <td style={numTd}>{fmtN(r.committed)}</td>
                                        <td style={numTd}>{fmtN(r.available)}</td>
                                        <td style={numTd}>{fmtN(r.on_hand)}</td>
                                        <td style={numTd}>{fmtN(r.incoming)}</td>
                                        {stores.map(st => <td key={st.key} style={{ ...numTd, color: (r.sold[st.key] || 0) === 0 ? '#8c9196' : undefined }}>
                                            {st.blank ? '' : fmtN(r.sold[st.key] || 0)}</td>)}
                                        <td style={{ ...numTd, fontWeight: 600 }}>{fmtN(r.total_sold)}</td>
                                    </tr>
                                ))}
                                {data.rows.length === 0 && <tr><td colSpan={FIXED.length + stores.length + 1} style={{ padding: 24, textAlign: 'center', color: '#6d7175' }}>No SKUs match these filters.</td></tr>}
                            </tbody>
                            {data.total > 0 && (
                                <tfoot>
                                    <tr style={{ fontWeight: 600 }}>
                                        <td colSpan={6}>Total ({fmtN(data.total)} SKUs)</td>
                                        <td style={numTd}>{fmtN(data.totals.committed)}</td><td style={numTd}>{fmtN(data.totals.available)}</td>
                                        <td style={numTd}>{fmtN(data.totals.on_hand)}</td><td style={numTd}>{fmtN(data.totals.incoming)}</td>
                                        {stores.map(st => <td key={st.key} style={numTd}>{st.blank ? '' : fmtN(data.totals.sold[st.key])}</td>)}
                                        <td style={numTd}>{fmtN(data.totals.total_sold)}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                    {pages > 1 && (
                        <div style={{ padding: 10, borderTop: '1px solid #e1e3e5' }}>
                            <InlineStack align="space-between" blockAlign="center">
                                <Button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Previous</Button>
                                <Text as="span" tone="subdued">Page {page} of {pages}</Text>
                                <Button disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next →</Button>
                            </InlineStack>
                        </div>
                    )}
                </Card>
            )}
        </BlockStack>
    );
}
